import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Corporate Identity ───────────────────────────────────────────────────────
const C = {
  black:      [10, 10, 10],
  darkBg:     [17, 17, 17],
  cardBg:     [248, 248, 248],
  cardBg2:    [240, 240, 240],
  green:      [34, 139, 86],
  greenLight: [72, 168, 120],
  greenBg:    [220, 242, 234],
  gold:       [180, 140, 60],
  goldLight:  [210, 180, 100],
  white:      [255, 255, 255],
  offWhite:   [250, 249, 246],
  gray:       [130, 130, 130],
  grayLight:  [180, 180, 180],
  grayDark:   [80, 80, 80],
  grayBorder: [220, 220, 220],
  red:        [200, 60, 60],
  redBg:      [252, 228, 228],
  yellow:     [180, 140, 20],
  yellowBg:   [252, 244, 215],
  rowGreen:   [220, 242, 234],
  rowYellow:  [252, 244, 215],
  rowRed:     [252, 228, 228],
  textDark:   [30, 30, 30],
  textMid:    [70, 70, 70],
};

const COMPANY = {
  name:    'Las Dos Doncellas',
  tagline: 'Productos Ibéricos de Calidad Superior',
  web:     'www.lasdosdoncellas.es',
  slogan:  'Comprometidos con la máxima calidad, trazabilidad y aprovechamiento de cada pieza.',
};

// ─── Page dimensions (A4 Portrait) ───────────────────────────────────────────
const PW = 210;  // portrait width
const PH = 297;  // portrait height
const M  = 12;   // margin

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v, dec = 2) => {
  const n = parseFloat(v);
  return isNaN(n) ? '-' : n.toFixed(dec);
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
  catch { return '-'; }
};

const getWeekNumber = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const rendColor = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return C.gray;
  if (v >= 50) return C.green;
  if (v >= 40) return C.yellow;
  return C.red;
};

const rendBgColor = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return C.cardBg2;
  if (v >= 50) return C.greenBg;
  if (v >= 40) return C.yellowBg;
  return C.redBg;
};

const rendLabel = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return '-';
  if (v >= 50) return 'Excelente';
  if (v >= 40) return 'Correcto';
  return 'Bajo';
};

const rendEmoji = (r) => {
  const v = parseFloat(r);
  if (isNaN(v)) return '';
  if (v >= 50) return '🟢';
  if (v >= 40) return '🟡';
  return '🔴';
};

// ─── Logo loader ──────────────────────────────────────────────────────────────
const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width; c.height = img.height;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = '/assets/images/logo_las_dos_doncellas_1MB-1784658867185.jpg';
  });

const loadImageFromUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/jpeg'));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// ─── Drawing primitives ───────────────────────────────────────────────────────
const fillRect = (doc, x, y, w, h, color) => {
  doc.setFillColor(...color);
  doc.rect(x, y, w, h, 'F');
};

const strokeRect = (doc, x, y, w, h, color, lw = 0.3) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(lw);
  doc.rect(x, y, w, h, 'S');
};

const txt = (doc, str, x, y, opts = {}) => {
  doc.text(String(str ?? ''), x, y, opts);
};

// ─── Page Header (portrait) ───────────────────────────────────────────────────
const drawPageHeader = (doc, logo, clienteName, periodo, pageNum, totalPages) => {
  // Gold top bar
  fillRect(doc, 0, 0, PW, 1.5, C.gold);
  // White header area
  fillRect(doc, 0, 1.5, PW, 22, C.white);
  // Bottom border
  fillRect(doc, 0, 23, PW, 0.4, C.grayBorder);

  // Logo
  if (logo) {
    try { doc.addImage(logo, 'PNG', M, 3, 16, 16); } catch {}
  }

  // Company name
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gold);
  txt(doc, COMPANY.name.toUpperCase(), M + 20, 9);

  // Title
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'INFORME SEMANAL DE LONCHEADOS', M + 20, 16);

  // Right: client + period
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'CLIENTE', PW - M, 8, { align: 'right' });
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, clienteName || 'Todos los clientes', PW - M, 14, { align: 'right' });

  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, periodo, PW - M, 20, { align: 'right' });
};

// ─── Page Footer (portrait) ───────────────────────────────────────────────────
const drawPageFooter = (doc, pageNum, totalPages) => {
  fillRect(doc, 0, PH - 9, PW, 9, C.offWhite);
  fillRect(doc, 0, PH - 9, PW, 0.4, C.grayBorder);
  fillRect(doc, 0, PH - 0.8, PW, 0.8, C.gold);

  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, `${COMPANY.name}  ·  ${COMPANY.web}`, M, PH - 3.5);
  txt(doc, `Pág. ${pageNum} / ${totalPages}`, PW - M, PH - 3.5, { align: 'right' });
  doc.setFontSize(5);
  doc.setTextColor(...C.grayLight);
  txt(doc, `"${COMPANY.slogan}"`, PW / 2, PH - 3.5, { align: 'center' });
};

// ═══════════════════════════════════════════════════════════════════════════════
// PIEZA PAGE: One full A4 portrait page per loncheado
// ═══════════════════════════════════════════════════════════════════════════════
const drawPiezaPage = async (doc, logo, pieza, clienteName, periodo, pageNum, totalPages) => {
  fillRect(doc, 0, 0, PW, PH, C.white);
  drawPageHeader(doc, logo, clienteName, periodo, pageNum, totalPages);

  const contentStartY = 26;
  const contentEndY = PH - 11;
  const usableW = PW - M * 2;

  // ── Piece header bar ──────────────────────────────────────────────────────
  fillRect(doc, M, contentStartY, usableW, 12, C.textDark);
  fillRect(doc, M, contentStartY, 3, 12, C.gold);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  const prodName = (pieza.producto || 'Producto').substring(0, 40);
  txt(doc, prodName, M + 6, contentStartY + 5);

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.goldLight);
  txt(doc, `Cliente: ${pieza.cliente || '-'}`, M + 6, contentStartY + 10);

  // Date + lote on right
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.grayLight);
  txt(doc, `Fecha: ${pieza.fecha}`, PW - M - 2, contentStartY + 5, { align: 'right' });
  if (pieza.lote) {
    txt(doc, `Lote: ${pieza.lote}`, PW - M - 2, contentStartY + 10, { align: 'right' });
  }

  // ── Main photo area (~55% of usable height) ───────────────────────────────
  const photoY = contentStartY + 14;
  const photoH = Math.round((contentEndY - contentStartY - 14) * 0.54);
  const photoW = usableW;

  if (pieza.imageData) {
    try {
      // Gold border frame
      fillRect(doc, M - 1, photoY - 1, photoW + 2, photoH + 2, C.gold);
      doc.addImage(pieza.imageData, 'JPEG', M, photoY, photoW, photoH, undefined, 'FAST');
    } catch {
      fillRect(doc, M, photoY, photoW, photoH, C.cardBg2);
      doc.setFontSize(8);
      doc.setTextColor(...C.gray);
      txt(doc, 'Fotografía no disponible', PW / 2, photoY + photoH / 2, { align: 'center' });
    }
  } else {
    // No photo placeholder
    fillRect(doc, M, photoY, photoW, photoH, C.cardBg2);
    strokeRect(doc, M, photoY, photoW, photoH, C.grayBorder, 0.5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    txt(doc, 'Sin fotografía registrada', PW / 2, photoY + photoH / 2, { align: 'center' });
  }

  // ── Info section below photo ──────────────────────────────────────────────
  const infoY = photoY + photoH + 4;
  const infoH = contentEndY - infoY;

  // Split into left (ficha técnica) and right (cortador + aprovechamiento)
  const leftW = usableW * 0.55;
  const rightW = usableW - leftW - 4;
  const leftX = M;
  const rightX = M + leftW + 4;

  // ── LEFT: Ficha Técnica ───────────────────────────────────────────────────
  fillRect(doc, leftX, infoY, leftW, infoH, C.white);
  strokeRect(doc, leftX, infoY, leftW, infoH, C.grayBorder, 0.3);

  // Section title
  fillRect(doc, leftX, infoY, leftW, 7, C.offWhite);
  fillRect(doc, leftX, infoY, 2.5, 7, C.gold);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'FICHA TÉCNICA', leftX + 5, infoY + 5);

  const fields = [
    { label: 'Producto',          value: pieza.producto || '-' },
    { label: 'Fecha',             value: pieza.fecha || '-' },
    { label: 'Peso Inicial',      value: `${fmt(pieza.pesoInicial, 2)} kg` },
    { label: 'Peso Aprovechado',  value: `${fmt(pieza.pesoAprovechado, 2)} kg` },
    { label: 'Merma',             value: `${fmt(pieza.merma, 2)} kg` },
    { label: 'Rendimiento',       value: `${fmt(pieza.rendimiento, 1)}%`, highlight: true },
    { label: 'Categoría',         value: pieza.categoria || '-' },
    { label: 'Lote',              value: pieza.lote || '-' },
  ];

  const fieldH = (infoH - 9) / fields.length;
  fields.forEach((f, i) => {
    const fy = infoY + 8 + i * fieldH;

    // Alternating row bg
    if (i % 2 === 0) fillRect(doc, leftX + 0.5, fy - 0.5, leftW - 1, fieldH, C.cardBg);

    // Label
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    txt(doc, f.label.toUpperCase(), leftX + 4, fy + fieldH * 0.65);

    // Value
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    if (f.highlight) {
      doc.setTextColor(...rendColor(pieza.rendimiento));
    } else {
      doc.setTextColor(...C.textDark);
    }
    txt(doc, f.value, leftX + leftW - 4, fy + fieldH * 0.65, { align: 'right' });

    // Separator
    doc.setDrawColor(...C.grayBorder);
    doc.setLineWidth(0.15);
    doc.line(leftX + 2, fy + fieldH, leftX + leftW - 2, fy + fieldH);
  });

  // ── RIGHT: Cortador + Aprovechamiento ─────────────────────────────────────
  // Cortador block (top half of right)
  const cortadorBlockH = Math.round(infoH * 0.52);
  fillRect(doc, rightX, infoY, rightW, cortadorBlockH, C.white);
  strokeRect(doc, rightX, infoY, rightW, cortadorBlockH, C.grayBorder, 0.3);

  // Title
  fillRect(doc, rightX, infoY, rightW, 7, C.textDark);
  fillRect(doc, rightX, infoY, 2.5, 7, C.gold);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  txt(doc, 'MAESTRO CORTADOR', rightX + 5, infoY + 5);

  // Cortador avatar circle
  const avatarCX = rightX + rightW / 2;
  const avatarCY = infoY + cortadorBlockH * 0.35;
  const avatarR = Math.min(rightW * 0.18, 14);

  doc.setFillColor(...C.gold);
  doc.circle(avatarCX, avatarCY, avatarR + 1, 'F');
  doc.setFillColor(...C.textDark);
  doc.circle(avatarCX, avatarCY, avatarR, 'F');

  // Initials
  const cortadorName = pieza.empleado || 'Sin asignar';
  const initials = cortadorName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.gold);
  txt(doc, initials, avatarCX, avatarCY + 3.5, { align: 'center' });

  // Cortador name
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  const cortLines = doc.splitTextToSize(cortadorName, rightW - 6);
  txt(doc, cortLines[0] || '', rightX + rightW / 2, infoY + cortadorBlockH * 0.62, { align: 'center' });
  if (cortLines[1]) {
    txt(doc, cortLines[1], rightX + rightW / 2, infoY + cortadorBlockH * 0.72, { align: 'center' });
  }

  // Subtitle
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'Loncheado realizado por', rightX + rightW / 2, infoY + cortadorBlockH * 0.53, { align: 'center' });

  // Date
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, pieza.fecha, rightX + rightW / 2, infoY + cortadorBlockH * 0.88, { align: 'center' });

  // ── Aprovechamiento visual bar ─────────────────────────────────────────────
  const aprovY = infoY + cortadorBlockH + 3;
  const aprovH = infoH - cortadorBlockH - 3;
  fillRect(doc, rightX, aprovY, rightW, aprovH, C.white);
  strokeRect(doc, rightX, aprovY, rightW, aprovH, C.grayBorder, 0.3);

  // Title
  fillRect(doc, rightX, aprovY, rightW, 7, C.offWhite);
  fillRect(doc, rightX, aprovY, 2.5, 7, C.gold);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'APROVECHAMIENTO', rightX + 5, aprovY + 5);

  // Rendimiento value big
  const rend = parseFloat(pieza.rendimiento) || 0;
  const mermaP = 100 - rend;

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rendColor(rend));
  txt(doc, `${fmt(rend, 1)}%`, rightX + rightW / 2, aprovY + 20, { align: 'center' });

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, rendLabel(rend).toUpperCase(), rightX + rightW / 2, aprovY + 26, { align: 'center' });

  // Visual bar
  const barX = rightX + 4;
  const barW2 = rightW - 8;
  const barY = aprovY + 30;
  const barH = 7;

  // Background (merma = red)
  fillRect(doc, barX, barY, barW2, barH, C.red);
  // Aprovechado (green)
  const aprovW = Math.max(0, Math.min(barW2, (rend / 100) * barW2));
  fillRect(doc, barX, barY, aprovW, barH, C.green);

  // Bar border
  strokeRect(doc, barX, barY, barW2, barH, C.grayBorder, 0.2);

  // Labels below bar
  doc.setFontSize(5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.green);
  txt(doc, `${fmt(rend, 1)}% Aprovechado`, barX, barY + barH + 4);
  doc.setTextColor(...C.red);
  txt(doc, `${fmt(mermaP, 1)}% Merma`, barX + barW2, barY + barH + 4, { align: 'right' });

  // Observaciones if any
  if (pieza.observaciones) {
    const obsY = aprovY + 46;
    if (obsY < aprovY + aprovH - 4) {
      doc.setFontSize(5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...C.gray);
      const obsLines = doc.splitTextToSize(`Obs: ${pieza.observaciones}`, rightW - 6);
      obsLines.slice(0, 2).forEach((line, li) => {
        txt(doc, line, rightX + 3, obsY + li * 4.5);
      });
    }
  }

  drawPageFooter(doc, pageNum, totalPages);
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUMMARY PAGE: Last page with table of all pieces
// ═══════════════════════════════════════════════════════════════════════════════
const drawSummaryPage = (doc, logo, piezasData, stats, clienteName, periodo, pageNum, totalPages) => {
  fillRect(doc, 0, 0, PW, PH, C.white);
  drawPageHeader(doc, logo, clienteName, periodo, pageNum, totalPages);

  const contentY = 26;
  const usableW = PW - M * 2;

  // ── KPI summary strip ─────────────────────────────────────────────────────
  const kpiH = 18;
  const kpiData = [
    { label: 'Piezas', value: String(stats.totalPiezas), color: C.gold },
    { label: 'Peso Bruto', value: `${fmt(stats.pesoInicial, 1)} kg`, color: [100, 149, 237] },
    { label: 'Peso Neto', value: `${fmt(stats.pesoAprovechado, 1)} kg`, color: C.green },
    { label: 'Merma Total', value: `${fmt(stats.mermaTotal, 1)} kg`, color: C.red },
    { label: 'Rendimiento Medio', value: `${fmt(stats.rendimientoMedio, 1)}%`, color: rendColor(stats.rendimientoMedio) },
  ];

  const kpiW = (usableW - 4 * 3) / 5;
  kpiData.forEach((k, i) => {
    const kx = M + i * (kpiW + 3);
    fillRect(doc, kx, contentY, kpiW, kpiH, C.white);
    strokeRect(doc, kx, contentY, kpiW, kpiH, C.grayBorder, 0.3);
    fillRect(doc, kx, contentY, kpiW, 1.5, k.color);

    doc.setFontSize(5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.gray);
    txt(doc, k.label.toUpperCase(), kx + kpiW / 2, contentY + 7, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...k.color);
    txt(doc, k.value, kx + kpiW / 2, contentY + 14, { align: 'center' });
  });

  // ── Section title ─────────────────────────────────────────────────────────
  const tableHeaderY = contentY + kpiH + 5;
  fillRect(doc, M, tableHeaderY, usableW, 7, C.textDark);
  fillRect(doc, M, tableHeaderY, 3, 7, C.gold);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  txt(doc, 'RESUMEN GENERAL DE PIEZAS', M + 6, tableHeaderY + 5);

  // ── Summary table ─────────────────────────────────────────────────────────
  const tableRows = piezasData.map((p, idx) => [
    String(idx + 1),
    p.producto || '-',
    p.fecha || '-',
    p.empleado || '-',
    `${fmt(p.pesoInicial, 2)} kg`,
    `${fmt(p.pesoAprovechado, 2)} kg`,
    `${fmt(p.merma, 2)} kg`,
    `${fmt(p.rendimiento, 1)}%`,
  ]);

  autoTable(doc, {
    startY: tableHeaderY + 8,
    head: [['#', 'Producto', 'Fecha', 'Cortador', 'Peso Inicial', 'Peso Aprovechado', 'Merma', 'Rendimiento']],
    body: tableRows,
    margin: { left: M, right: M },
    tableWidth: usableW,
    styles: {
      font: 'helvetica',
      fontSize: 6,
      cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
      textColor: C.textDark,
      lineColor: C.grayBorder,
      lineWidth: 0.15,
    },
    headStyles: {
      fillColor: C.grayDark,
      textColor: C.white,
      fontStyle: 'bold',
      fontSize: 6,
      halign: 'center',
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { cellWidth: 'auto' },
      2: { halign: 'center', cellWidth: 20 },
      3: { cellWidth: 30 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'right', cellWidth: 26 },
      6: { halign: 'right', cellWidth: 18 },
      7: { halign: 'center', cellWidth: 22, fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const rend = piezasData[data.row.index]?.rendimiento;
        if (data.column.index === 7) {
          data.cell.styles.textColor = rendColor(rend);
        }
        if (data.row.index % 2 === 0) {
          data.cell.styles.fillColor = C.cardBg;
        }
      }
    },
  });

  // ── Closing message ───────────────────────────────────────────────────────
  const msgY = PH - 30;
  fillRect(doc, M, msgY, usableW, 18, C.offWhite);
  strokeRect(doc, M, msgY, usableW, 18, C.grayBorder, 0.3);
  fillRect(doc, M, msgY, 3, 18, C.gold);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.textDark);
  txt(doc, 'Gracias por confiar en Las Dos Doncellas', PW / 2, msgY + 7, { align: 'center' });

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.gray);
  txt(doc, 'Nuestro objetivo es garantizar la máxima calidad, trazabilidad y aprovechamiento de cada pieza ibérica.', PW / 2, msgY + 13, { align: 'center' });

  drawPageFooter(doc, pageNum, totalPages);
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTION
// ═══════════════════════════════════════════════════════════════════════════════
export const exportLoncheadosPDF = async ({
  filteredLoncheados = [],
  clientes = [],
  empleados = [],
  productos = [],
  filters = {},
}) => {
  const logo = await loadLogo();

  // ── Resolve names ──────────────────────────────────────────────────────────
  const getCliente  = (id) => clientes.find(c => c.id === id)?.nombre || '-';
  const getEmpleado = (id) => empleados.find(e => e.id === id)?.nombre || '-';
  const getProducto = (id) => productos.find(p => p.id === id)?.nombre || '-';

  // ── Client name ────────────────────────────────────────────────────────────
  const clienteNombre = filters?.clienteId && filters.clienteId !== 'all'
    ? (clientes.find(c => c.id === filters.clienteId)?.nombre || 'Cliente')
    : 'Todos los clientes';

  // ── Date range ─────────────────────────────────────────────────────────────
  let periodo = 'Todos los registros';
  if (filteredLoncheados.length > 0) {
    const dates = filteredLoncheados.map(l => new Date(l.createdAt)).sort((a, b) => a - b);
    const from = fmtDate(dates[0]);
    const to   = fmtDate(dates[dates.length - 1]);
    periodo = from === to ? from : `${from} – ${to}`;
  }

  // ── Compute global stats ───────────────────────────────────────────────────
  const totalPiezas     = filteredLoncheados.length;
  const pesoInicial     = filteredLoncheados.reduce((s, l) => s + (parseFloat(l.pesoInicial) || 0), 0);
  const pesoAprovechado = filteredLoncheados.reduce((s, l) => s + (parseFloat(l.pesoAprovechado) || 0), 0);
  const mermaTotal      = filteredLoncheados.reduce((s, l) => s + (parseFloat(l.merma) || 0), 0);
  const rendValues      = filteredLoncheados.map(l => parseFloat(l.rendimiento)).filter(v => !isNaN(v) && v > 0);
  const rendimientoMedio = rendValues.length > 0 ? rendValues.reduce((a, b) => a + b, 0) / rendValues.length : 0;

  const stats = {
    totalPiezas, pesoInicial, pesoAprovechado, mermaTotal, rendimientoMedio,
  };

  // ── Prepare piece data with photos ────────────────────────────────────────
  const piezasData = await Promise.all(
    filteredLoncheados
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map(async (l) => ({
        producto:        getProducto(l.productoId),
        cliente:         getCliente(l.clienteId),
        empleado:        getEmpleado(l.empleadoId),
        fecha:           fmtDate(l.createdAt),
        pesoInicial:     l.pesoInicial,
        pesoAprovechado: l.pesoAprovechado,
        merma:           l.merma,
        rendimiento:     l.rendimiento,
        lote:            l.lote || '',
        categoria:       l.categoria || '',
        observaciones:   l.observaciones || '',
        imageData:       l.fotoUrl ? await loadImageFromUrl(l.fotoUrl) : null,
      }))
  );

  // ── Total pages = one per pieza + 1 summary page ──────────────────────────
  const totalPages = piezasData.length + 1;

  // ── Build PDF (A4 Portrait) ───────────────────────────────────────────────
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // One page per pieza
  for (let i = 0; i < piezasData.length; i++) {
    if (i > 0) doc.addPage();
    await drawPiezaPage(doc, logo, piezasData[i], clienteNombre, periodo, i + 1, totalPages);
  }

  // Summary page (last)
  if (piezasData.length > 0) {
    doc.addPage();
    drawSummaryPage(doc, logo, piezasData, stats, clienteNombre, periodo, totalPages, totalPages);
  } else {
    // No data fallback
    fillRect(doc, 0, 0, PW, PH, C.white);
    drawPageHeader(doc, logo, clienteNombre, periodo, 1, 1);
    doc.setFontSize(10);
    doc.setTextColor(...C.gray);
    txt(doc, 'No hay piezas registradas para este periodo.', PW / 2, PH / 2, { align: 'center' });
    drawPageFooter(doc, 1, 1);
  }

  // ── Filename ───────────────────────────────────────────────────────────────
  const weekNum = getWeekNumber(new Date());
  const year    = new Date().getFullYear();
  const safeCliente = clienteNombre.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]/g, '_').substring(0, 30);
  const filename = `Informe_Loncheado_${safeCliente}_Semana${weekNum}_${year}.pdf`;

  doc.save(filename);
};
